import { useEffect, useState } from "react";
import { api, fmtINR } from "../../lib/api";
import { StoreLayout } from "../../components/StoreLayout";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  ArrowRight, ShieldCheck, Clock, Star, CheckCircle, Quotes, MapPin, Sparkle,
} from "@phosphor-icons/react";
import {
  HERO_IMAGE, categoryImage, mrp, discountPct, TRUST_STATS, REVIEWS,
} from "../../lib/storefront";
import { IndiaPartnerMap } from "../../components/IndiaPartnerMap";
import { AvailableServices, PopularCities } from "../../components/store/HomeServiceability";

const Stars = ({ n, size = 14 }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} weight={i <= n ? "fill" : "regular"} className={i <= n ? "text-[#166534]" : "text-[#94a3b8]"} />
    ))}
  </span>
);

export default function Home() {
  const [catalog, setCatalog] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { api.get("/public/services").then((r) => setCatalog(r.data)); }, []);

  const featured = catalog.flatMap((c) =>
    c.product_types.flatMap((pt) => pt.services.filter((s) => s.featured)
      .map((s) => ({ ...s, category: c.category, product_type: pt.product_type })))
  ).slice(0, 4);

  return (
    <StoreLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 pt-14 pb-16 md:pt-20 md:pb-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <span className="inline-flex items-center gap-2 uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5] bg-[#4F46E5]/10 px-3.5 py-1.5 rounded-full">
              <Sparkle size={13} weight="fill" /> Loved by 20,000+ happy homes
            </span>
            <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mt-5">
              Ghar ki har problem,<br /><span className="text-[#4F46E5]">ek tap mein solved.</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#475569] max-w-lg leading-relaxed">
              AC service, appliance repair, deep cleaning & pest control — delivered by verified Fixigo professionals at your doorstep. Upfront pricing, pay after service.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => navigate("/services")} data-testid="hero-book-now"
                className="rounded-xl bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold h-12 px-7 gap-2 shadow-lg shadow-[#4F46E5]/30">
                Book a Service <ArrowRight size={18} weight="bold" />
              </Button>
              <Button onClick={() => navigate("/become-partner")} variant="outline" data-testid="hero-become-partner"
                className="rounded-xl border-[#1E293B]/15 bg-transparent text-[#1E293B] hover:bg-[#1E293B]/5 h-12 px-7 font-bold">
                Become a Partner
              </Button>
            </div>
            <div className="mt-9 flex flex-wrap gap-x-7 gap-y-3 text-sm font-medium text-[#475569]">
              <div className="flex items-center gap-2"><ShieldCheck size={18} weight="fill" className="text-[#166534]" /> Verified pros</div>
              <div className="flex items-center gap-2"><Clock size={18} weight="fill" className="text-[#166534]" /> Same-day slots</div>
              <div className="flex items-center gap-2"><Star size={18} weight="fill" className="text-[#166534]" /> Upfront pricing</div>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <img src={HERO_IMAGE} alt="Happy Indian family at home" className="rounded-2xl w-full h-[300px] md:h-[440px] object-cover shadow-2xl" />
            <div className="absolute -bottom-5 -left-3 md:left-[-28px] bg-white rounded-2xl shadow-xl border border-[#E2E8F0] px-5 py-4 w-[230px]">
              <div className="flex items-center gap-2">
                <span className="font-[Outfit] text-3xl font-extrabold">{TRUST_STATS.rating}</span>
                <div><Stars n={4} /><p className="text-[11px] text-[#475569] mt-0.5">average rating</p></div>
              </div>
              <div className="h-px bg-[#E2E8F0] my-3" />
              <p className="text-sm font-semibold"><span className="text-[#4F46E5]">{TRUST_STATS.customers}</span> happy customers served</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust stats strip */}
      <section className="border-y border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-5 py-8 grid grid-cols-3 gap-6 text-center">
          {[
            [TRUST_STATS.customers, "Customers served"],
            [`${TRUST_STATS.rating} ★`, "Average rating"],
            [TRUST_STATS.pros, "Verified professionals"],
          ].map(([v, l], i) => (
            <div key={i} data-testid={`trust-stat-${i}`}>
              <p className="font-[Outfit] text-3xl md:text-4xl font-extrabold text-[#1E293B]">{v}</p>
              <p className="text-sm text-[#475569] mt-1">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services available for the selected location (header) */}
      <AvailableServices />

      {/* Live partner coverage map */}
      <section className="max-w-7xl mx-auto px-5 py-16 md:py-20">
        <IndiaPartnerMap />
      </section>

      {/* Popular Cities */}
      <PopularCities />

      {/* Pictorial Categories */}
      <section className="max-w-7xl mx-auto px-5 py-16 md:py-20">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5]">Browse services</p>
            <h2 className="font-[Outfit] text-3xl md:text-4xl font-bold tracking-tight mt-2">What do you need help with?</h2>
          </div>
          <Button variant="ghost" onClick={() => navigate("/services")} className="rounded-xl gap-1.5 text-[#4F46E5] hover:bg-[#4F46E5]/10 hover:text-[#4338CA]">View all <ArrowRight size={16} weight="bold" /></Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-9" data-testid="category-grid">
          {catalog.map((c) => {
            const count = c.product_types.reduce((a, p) => a + p.services.length, 0);
            return (
              <button key={c.category} onClick={() => navigate(`/services?category=${encodeURIComponent(c.category)}`)}
                data-testid={`home-cat-${c.category.replace(/ /g, "-")}`}
                className="group relative aspect-[4/5] rounded-2xl overflow-hidden text-left shadow-sm hover:shadow-xl transition-all duration-300">
                <img src={categoryImage(c.category)} alt={c.category} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                  <h3 className="font-[Outfit] font-bold text-lg text-white leading-tight">{c.category}</h3>
                  <p className="text-xs text-white/75 mt-0.5">{count} service{count !== 1 ? "s" : ""}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-[#C7D2FE] mt-2 group-hover:gap-2 transition-all duration-200">
                    Explore <ArrowRight size={14} weight="bold" />
                  </span>
                </div>
              </button>
            );
          })}
          {catalog.length === 0 && <p className="text-[#475569] col-span-4">Services coming soon.</p>}
        </div>
      </section>

      {/* Popular services (pictorial) */}
      {featured.length > 0 && (
        <section className="bg-white border-y border-[#E2E8F0] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-5">
            <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5]">Most booked</p>
            <h2 className="font-[Outfit] text-3xl md:text-4xl font-bold tracking-tight mt-2">Popular this week</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mt-9">
              {featured.map((s, i) => {
                const off = discountPct(s.amount);
                return (
                  <div key={i} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl overflow-hidden flex flex-col hover:shadow-lg transition-shadow duration-200" data-testid={`featured-${i}`}>
                    <div className="relative h-40">
                      <img src={categoryImage(s.category)} alt={s.category} loading="lazy" className="w-full h-full object-cover" />
                      {off > 0 && <span className="absolute top-3 left-3 bg-[#166534] text-white text-[11px] font-bold px-2.5 py-1 rounded-full">{off}% OFF</span>}
                      <span className="absolute top-3 right-3 bg-white/90 backdrop-blur text-[11px] font-bold px-2 py-1 rounded-full flex items-center gap-1"><Star size={11} weight="fill" className="text-[#4F46E5]" /> Popular</span>
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h3 className="font-[Outfit] font-bold">{s.service_type}</h3>
                      <p className="text-xs text-[#475569]">{s.category} · {s.product_type}</p>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E2E8F0]">
                        <div>
                          <span className="font-[Outfit] font-extrabold text-lg">{fmtINR(s.amount)}</span>
                          {off > 0 && <span className="text-xs text-[#94a3b8] line-through ml-1.5">{fmtINR(mrp(s.amount))}</span>}
                        </div>
                        <Button size="sm" className="rounded-xl bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold" onClick={() => navigate(`/book?category=${encodeURIComponent(s.category)}&type=${encodeURIComponent(s.product_type)}&service=${encodeURIComponent(s.service_type)}&amount=${s.amount}`)} data-testid={`featured-book-${i}`}>Book</Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 py-16 md:py-20">
        <h2 className="font-[Outfit] text-3xl md:text-4xl font-bold tracking-tight text-center">Booking in 3 simple steps</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
          {[
            ["1", "Choose a service", "Browse pictorial categories and add the service you need to your cart."],
            ["2", "Pick a slot & address", "Tell us where and when. Pay only after the service is done."],
            ["3", "Pro arrives & fixes it", "A verified Fixigo partner completes the job. Track it anytime."],
          ].map(([n, t, d]) => (
            <div key={n} className="text-center relative">
              <div className="w-14 h-14 rounded-2xl bg-[#4F46E5] text-white font-[Outfit] font-extrabold text-xl flex items-center justify-center mx-auto shadow-lg shadow-[#4F46E5]/25">{n}</div>
              <h3 className="font-[Outfit] font-bold text-lg mt-5">{t}</h3>
              <p className="text-sm text-[#475569] mt-1.5 max-w-xs mx-auto leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="bg-white border-y border-[#E2E8F0] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5]">Loved across India</p>
              <h2 className="font-[Outfit] text-3xl md:text-4xl font-bold tracking-tight mt-2">What our customers say</h2>
            </div>
            <div className="flex items-center gap-3 bg-[#166534]/8 border border-[#166534]/20 rounded-2xl px-5 py-3">
              <span className="font-[Outfit] text-4xl font-extrabold text-[#166534]">{TRUST_STATS.rating}</span>
              <div>
                <Stars n={4} size={16} />
                <p className="text-xs text-[#475569] mt-1">from {TRUST_STATS.customers} customers</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="reviews-grid">
            {REVIEWS.map((r, i) => (
              <div key={i} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-6 flex flex-col" data-testid={`review-${i}`}>
                <Quotes size={26} weight="fill" className="text-[#4F46E5]/30" />
                <p className="text-sm text-[#1E293B] leading-relaxed mt-2 flex-1">{r.text}</p>
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#E2E8F0]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#4F46E5]/15 text-[#4F46E5] font-[Outfit] font-bold flex items-center justify-center">{r.name.charAt(0)}</div>
                    <div>
                      <p className="font-bold text-sm leading-tight">{r.name}</p>
                      <p className="text-xs text-[#475569] flex items-center gap-1"><MapPin size={11} weight="fill" /> {r.city}</p>
                    </div>
                  </div>
                  <Stars n={r.stars} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner CTA */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="bg-[#1E293B] rounded-3xl px-7 md:px-12 py-12 flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden relative">
          <div className="relative">
            <h2 className="font-[Outfit] text-3xl md:text-4xl font-bold tracking-tight text-white">Are you a service professional?</h2>
            <p className="text-white/70 mt-2.5 max-w-lg">Join {TRUST_STATS.pros} Fixigo partners, get verified jobs in your area, and grow your business.</p>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-white/80">
              {["Steady job flow", "On-time payments", "Free onboarding"].map((x) => (
                <li key={x} className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-[#4F46E5]" /> {x}</li>
              ))}
            </ul>
          </div>
          <Button onClick={() => navigate("/become-partner")} data-testid="cta-become-partner"
            className="rounded-xl bg-[#4F46E5] hover:bg-[#4338CA] text-white h-12 px-7 font-bold gap-2 shrink-0">
            Apply as Partner <ArrowRight size={18} weight="bold" />
          </Button>
        </div>
      </section>

      {/* Business division attribution */}
      <p data-testid="business-division-line" className="text-center text-xs md:text-sm text-[#94A3B8] pb-10 px-5">
        Fixigo Home Services, a business division of Mauli Enterprises.
      </p>
    </StoreLayout>
  );
}
